import { supabase } from "@/supabase";
import { storage_url_render } from "../utils/general";

// Stars Magnet talent recruitment (Management › Stars Magnet).
// Rows are every talent who has worked with Brandripplr, plus the Stars Magnet
// roster and talents added by hand. Computed columns come from sm_talents()
// (an hourly snapshot); status, lead and notes are live and written only through
// the sm_* RPCs (see the stars_magnet_* migrations).
export const SM_AGENCY_ID = 565;

const err = (e) => (e ? e.message || String(e) : null);
const fullName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(" ").trim() || u?.email || "Unknown";
const avatarUrl = (path) => {
  if (!path) return null;
  if (/^https?:/i.test(path)) return path;
  return storage_url_render + path + "?width=96&height=96&quality=75&resize=cover";
};

const shapeTalent = (t) => ({
  ...t,
  avatar: avatarUrl(t.avatar),
  channels: (t.channels || []).map((c) => ({
    ...c,
    username: (c.username || "").replace(/^@/, ""),
    followers: c.followers == null ? null : Number(c.followers),
    er: c.er == null ? null : Number(c.er),
  })),
  max_followers: t.max_followers == null ? null : Number(t.max_followers),
  audience_sa: t.audience_sa == null ? null : Number(t.audience_sa),
  audience_ae: t.audience_ae == null ? null : Number(t.audience_ae),
  avg_reel_fee: t.avg_reel_fee == null ? null : Number(t.avg_reel_fee),
  top_client: (t.clients || [])[0] || null,
});

export const StarsMagnetService = {
  // ------------------------------------------------------------------ access
  async getMyAccess() {
    const [m, a] = await Promise.all([supabase.rpc("sm_is_member"), supabase.rpc("sm_is_admin")]);
    if (m.error) return { member: false, admin: false, error: err(m.error) };
    return { member: !!m.data, admin: !!a.data, error: null };
  },

  // ----------------------------------------------------------------- talents
  // The API returns at most 1,000 rows per request, so the list is read in pages.
  async getTalents() {
    const PAGE = 1000;
    const first = await supabase.rpc("sm_talents", undefined, { count: "exact" }).range(0, PAGE - 1);
    if (first.error) return { talents: [], error: err(first.error) };
    let rows = first.data || [];
    if (first.count != null) {
      const pages = [];
      for (let from = rows.length; from < first.count; from += PAGE) {
        pages.push(supabase.rpc("sm_talents").range(from, from + PAGE - 1));
      }
      const results = await Promise.all(pages);
      const failed = results.find((r) => r.error);
      if (failed) return { talents: [], error: err(failed.error) };
      for (const r of results) rows = rows.concat(r.data || []);
    } else {
      // No count returned: keep reading until a short page
      let page = rows;
      while (page.length === PAGE) {
        const r = await supabase.rpc("sm_talents").range(rows.length, rows.length + PAGE - 1);
        if (r.error) return { talents: [], error: err(r.error) };
        page = r.data || [];
        rows = rows.concat(page);
      }
    }
    const seen = new Set();
    const talents = [];
    for (const t of rows) {
      if (seen.has(t.influencer_id)) continue;
      seen.add(t.influencer_id);
      talents.push(shapeTalent(t));
    }
    return { talents, error: null };
  },
  async getTalent(influencerId) {
    const { data, error } = await supabase.rpc("sm_talents", { p_influencer_id: influencerId });
    if (error) return { talent: null, error: err(error) };
    return { talent: data && data[0] ? shapeTalent(data[0]) : null, error: null };
  },
  async getEvents(influencerId) {
    const { data, error } = await supabase.rpc("sm_talent_events", { p_influencer_id: influencerId });
    return { events: data || [], error: err(error) };
  },

  // ------------------------------------------------------------------ writes
  async setStatus(influencerIds, { status, date = null, reason = null, reasonOther = null, note = null }) {
    const { data, error } = await supabase.rpc("sm_set_status", {
      p_influencer_ids: influencerIds,
      p_status: status,
      p_status_date: date,
      p_reason: reason,
      p_reason_other: reasonOther,
      p_note: note,
    });
    return { count: data || 0, error: err(error) };
  },
  async setLead(influencerIds, leadId) {
    const { data, error } = await supabase.rpc("sm_set_lead", { p_influencer_ids: influencerIds, p_lead_id: leadId || null });
    return { count: data || 0, error: err(error) };
  },
  async addNote(influencerId, note) {
    const { error } = await supabase.rpc("sm_add_note", { p_influencer_id: influencerId, p_note: note });
    return { error: err(error) };
  },

  // ----------------------------------------------------------------- members
  async getMembers() {
    const { data, error } = await supabase
      .from("sm_member")
      .select("user_id, is_admin, is_lead, created_at, users!sm_member_user_id_fkey(id, first_name, last_name, email, avatar)");
    if (error) return { members: [], error: err(error) };
    const members = (data || [])
      .map((m) => ({
        user_id: m.user_id,
        is_admin: m.is_admin,
        is_lead: m.is_lead,
        name: fullName(m.users),
        email: m.users?.email || "",
        avatar: avatarUrl(m.users?.avatar),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    return { members, error: null };
  },
  async searchStaff(query, excludeIds = []) {
    const term = (query || "").trim().replace(/[%,()]/g, " ");
    if (!term) return { users: [], error: null };
    const { data, error } = await supabase
      .from("users")
      .select("id, first_name, last_name, email")
      .eq("user_type", "staff")
      .eq("deactivate", false)
      .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%`)
      .limit(8);
    if (error) return { users: [], error: err(error) };
    return { users: (data || []).filter((u) => !excludeIds.includes(u.id)).map((u) => ({ ...u, name: fullName(u) })), error: null };
  },
  async addMember(userId, { isAdmin = false, isLead = true } = {}) {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("sm_member")
      .insert({ user_id: userId, is_admin: isAdmin, is_lead: isLead, added_by: auth?.user?.id || null });
    if (error) return { error: error.code === "23505" ? "Already a member" : err(error) };
    return { error: null };
  },
  async updateMember(userId, patch) {
    const row = {};
    if ("is_admin" in patch) row.is_admin = !!patch.is_admin;
    if ("is_lead" in patch) row.is_lead = !!patch.is_lead;
    const { error } = await supabase.from("sm_member").update(row).eq("user_id", userId);
    return { error: err(error) };
  },
  async removeMember(userId) {
    const { error } = await supabase.from("sm_member").delete().eq("user_id", userId);
    return { error: err(error) };
  },

  // ------------------------------------------------- linking talents to the agency
  // Brandripplr talents matching a name or handle that aren't on the list yet.
  async searchInfluencers(query, linkedIds = []) {
    const term = (query || "").trim().replace(/^@/, "").replace(/[%,()]/g, " ");
    if (term.length < 2) return { influencers: [], error: null };
    const [byName, byHandle] = await Promise.all([
      supabase
        .from("influencer")
        .select("id, first_name, last_name, avatar, network(social_network, username, follower_count)")
        .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%`)
        .limit(10),
      supabase
        .from("network")
        .select("influencer_id, influencer(id, first_name, last_name, avatar, network(social_network, username, follower_count))")
        .ilike("username", `%${term}%`)
        .limit(10),
    ]);
    if (byName.error) return { influencers: [], error: err(byName.error) };
    const seen = new Set(linkedIds);
    const out = [];
    const push = (inf) => {
      if (!inf || seen.has(inf.id)) return;
      seen.add(inf.id);
      out.push({
        id: inf.id,
        name: [inf.first_name, inf.last_name].filter(Boolean).join(" ").trim() || "Talent #" + inf.id,
        avatar: avatarUrl(inf.avatar),
        channels: (inf.network || [])
          .filter((n) => n.username)
          .sort((a, b) => (b.follower_count || 0) - (a.follower_count || 0))
          .slice(0, 3),
      });
    };
    (byName.data || []).forEach(push);
    (byHandle.data || []).forEach((n) => push(n.influencer));
    return { influencers: out.slice(0, 12), error: null };
  },
  // Puts a Brandripplr talent who hasn't worked on a campaign yet on the list.
  async addTalent(influencerId) {
    const { error } = await supabase.rpc("sm_add_talent", { p_influencer_id: influencerId });
    return { error: err(error) };
  },
};
